import * as React from 'react'
import * as HoverCardPrimitive from '@radix-ui/react-hover-card'
import { useMedia } from 'react-use'

import { cn } from '@/lib/utils'
import { CardContext } from 'app/frontend/base/components/Card'

const HoverCardSetOpenContext = React.createContext<
  ((open: boolean) => void) | null
>(null)

const HoverCard = ({
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Root>) => {
  const hasHover = useMedia('(hover: hover) and (pointer: fine)', true)

  const isControlled = open !== undefined
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false)

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next)
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange],
  )

  if (hasHover) {
    return (
      <HoverCardSetOpenContext.Provider value={null}>
        <HoverCardPrimitive.Root
          open={open}
          defaultOpen={defaultOpen}
          onOpenChange={onOpenChange}
          {...props}
        />
      </HoverCardSetOpenContext.Provider>
    )
  }

  return (
    <HoverCardSetOpenContext.Provider value={handleOpenChange}>
      <HoverCardPrimitive.Root
        open={isControlled ? open : internalOpen}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </HoverCardSetOpenContext.Provider>
  )
}

const HoverCardTrigger = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Trigger>
>(({ onClick, ...props }, ref) => {
  const setOpen = React.useContext(HoverCardSetOpenContext)

  return (
    <HoverCardPrimitive.Trigger
      ref={ref}
      onClick={(event) => {
        setOpen?.(true)
        onClick?.(event)
      }}
      {...props}
    />
  )
})
HoverCardTrigger.displayName = HoverCardPrimitive.Trigger.displayName

const HoverCardContent = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content> & {
    align?: string
  }
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => (
  <HoverCardPrimitive.Portal>
    <CardContext.Provider value={{ depth: 1 }}>
      <HoverCardPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'z-50 w-64 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-(--radix-hover-card-content-transform-origin)',
          className,
        )}
        {...props}
      />
    </CardContext.Provider>
  </HoverCardPrimitive.Portal>
))
HoverCardContent.displayName = HoverCardPrimitive.Content.displayName

export { HoverCard, HoverCardTrigger, HoverCardContent }
